import { useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Buttons } from '@/components/common/buttons'
import { InputImage } from '@/components/common/form/input-image'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import { Can } from '@/features/sessao/providers/auth-context'

type Repo<T> = {
  create: (params: { callId: number; imageStr: File }) => Promise<T>
}

type Props<T> = {
  callId: number
  repository: Repo<T>
  onSuccess: (data: T) => void
}

type FormValues = { images: { file: File }[] }

function CreateForm<T>({ callId, repository, onSuccess }: Props<T>) {
  const form = useForm<FormValues>({
    defaultValues: { images: [] },
  })

  const { fields, append, replace, remove } = useFieldArray({
    control: form.control,
    name: 'images',
  })

  const onSubmit = form.handleSubmit(async (data) => {
    if (!data.images[0]) return toast.error('Selecione uma imagem')

    try {
      const res = await repository.create({
        callId,
        imageStr: data.images[0].file,
      })
      onSuccess(res)
      form.reset()
    } catch {
      // Error toast is already shown by the API connector interceptor
    }
  })

  return (
    <Form {...form}>
      <form onSubmit={onSubmit}>
        <div className="flex flex-col gap-4">
          <InputImage
            field_control={form.control}
            field_name="images"
            field_label="Imagem"
            field_files={fields.map((field) => field.file)}
            field_append={(files) => append(files.map((file) => ({ file })))}
            field_replace={(files) => replace(files.map((file) => ({ file })))}
            field_remove={remove}
            field_maxFileSizeMb={5}
            field_maxFiles={1}
          />

          <div className="mt-8 flex justify-between">
            <Button type="submit">Enviar</Button>
          </div>
        </div>
      </form>
    </Form>
  )
}

export function EvidenceCreateDialog<T>({ onSuccess, ...props }: Props<T>) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Can asDefault I="create" a="CallReportAttachment">
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Buttons.Add />
        </DialogTrigger>

        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo</DialogTitle>
          </DialogHeader>

          <CreateForm
            {...props}
            onSuccess={(res) => {
              onSuccess(res)
            }}
          />
        </DialogContent>
      </Dialog>
    </Can>
  )
}
